import { pb } from '@/lib/pocketbase';
import { supabase } from '@/integrations/supabase/client';

/**
 * Extract device token from Bark URL or return the token if already extracted
 * Accepts: https://api.day.app/TOKEN/... or just TOKEN
 */
const extractBarkToken = (input: string): string => {
  const trimmed = input.trim();
  // Match URL pattern: https://api.day.app/TOKEN or https://api.day.app/TOKEN/...
  const urlMatch = trimmed.match(/api\.day\.app\/([^\/]+)/);
  if (urlMatch) {
    return urlMatch[1];
  }
  // Return as-is if it's just the token
  return trimmed;
};

/**
 * Bark notification service
 * Sends push notifications via Bark to iOS devices
 * Bark is a free iOS app for push notifications
 */
export const barkService = {
  /**
   * Get user's Bark device key from PocketBase
   */
  getUserBarkKey: async (userId: string): Promise<string | null> => {
    try {
      console.log('🔍 [BARK] Looking up barkKey for user:', userId);
      const user = await pb.collection('users').getOne(userId);
      console.log('🔍 [BARK] User record found, barkKey:', user.barkKey ? 'EXISTS' : 'NOT SET');
      return user.barkKey || null;
    } catch (error) {
      console.error('❌ [BARK] Error getting user bark key:', error);
      return null;
    }
  },

  /**
   * Save user's Bark device key to PocketBase (extracts token from URL if needed)
   */
  saveUserBarkKey: async (userId: string, barkInput: string): Promise<boolean> => {
    try {
      const barkKey = extractBarkToken(barkInput);
      await pb.collection('users').update(userId, { barkKey });
      console.log('✅ [BARK] Saved bark key for user:', userId);
      return true;
    } catch (error) {
      console.error('❌ [BARK] Error saving bark key:', error);
      return false;
    }
  },

  /**
   * Send notification to a user via Bark (if they have a key configured)
   */
  sendNotification: async (
    userId: string,
    title: string,
    message: string,
    options?: {
      sound?: string;
      icon?: string;
      url?: string;
      group?: string;
      isArchive?: boolean;
    }
  ): Promise<boolean> => {
    try {
      console.log('📣 [BARK] sendNotification called for user:', userId);
      
      // Get user's Bark device key
      const barkKey = await barkService.getUserBarkKey(userId);
      
      if (!barkKey) {
        console.log('⚠️ [BARK] User has no bark key configured:', userId);
        return false;
      }

      console.log('📣 [BARK] Found barkKey, sending notification to user:', userId);
      console.log('📣 [BARK] Invoking edge function with deviceKey:', barkKey.substring(0, 8) + '...');

      // Send via edge function
      const { data, error } = await supabase.functions.invoke('send-bark-notification', {
        body: {
          deviceKey: barkKey,
          title,
          body: message,
          sound: options?.sound || 'minuet',
          url: options?.url,
          group: options?.group || 'TutorNesta',
          isArchive: options?.isArchive ?? true
        }
      });

      if (error) {
        console.error('❌ [BARK] Edge function error:', error);
        return false;
      }

      console.log('✅ [BARK] Notification sent successfully:', data);
      return true;
    } catch (error) {
      console.error('❌ [BARK] Error sending notification:', error);
      return false;
    }
  },

  /**
   * Get base URL for notifications - uses custom domain in production
   */
  getBaseUrl: (): string => {
    // Use custom domain in production, fallback to current origin
    const customDomain = 'https://tutornesta.com';
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      return customDomain;
    }
    return typeof window !== 'undefined' ? window.location.origin : customDomain;
  },

  /**
   * Send message notification via Bark
   */
  sendMessageNotification: async (receiverId: string, senderName: string): Promise<boolean> => {
    console.log('🔔 [BARK] sendMessageNotification called:', { receiverId, senderName });
    const baseUrl = barkService.getBaseUrl();
    const result = await barkService.sendNotification(
      receiverId,
      '💬 New Message',
      `You have a new message from ${senderName}`,
      {
        sound: 'bell',
        url: `${baseUrl}/messages`,
        group: 'Messages'
      }
    );
    console.log('🔔 [BARK] sendMessageNotification result:', result);
    return result;
  },

  /**
   * Send booking confirmation notification via Bark
   */
  sendBookingNotification: async (
    userId: string,
    date: string,
    otherPartyName: string,
    isForStudent: boolean
  ): Promise<boolean> => {
    const baseUrl = barkService.getBaseUrl();
    const message = isForStudent
      ? `Your session with ${otherPartyName} on ${date} has been confirmed.`
      : `You have a new booking with ${otherPartyName} on ${date}.`;

    return barkService.sendNotification(
      userId,
      '📅 Booking Confirmed',
      message,
      {
        sound: 'fanfare',
        url: `${baseUrl}/schedule`,
        group: 'Bookings'
      }
    );
  },

  /**
   * Test Bark notification (direct to device key without user lookup)
   * Accepts full URL or just the token
   */
  sendTestNotification: async (barkInput: string): Promise<boolean> => {
    const deviceKey = extractBarkToken(barkInput);
    try {
      const { data, error } = await supabase.functions.invoke('send-bark-notification', {
        body: {
          deviceKey,
          title: '🎉 TutorNesta Test',
          body: 'If you see this, Bark notifications are working!',
          sound: 'bell',
          group: 'TutorNesta'
        }
      });

      if (error) {
        console.error('❌ [BARK] Test notification error:', error);
        return false;
      }

      console.log('✅ [BARK] Test notification sent:', data);
      return true;
    } catch (error) {
      console.error('❌ [BARK] Test notification failed:', error);
      return false;
    }
  }
};
